import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import InstitutionSelector from "../../components/InstitutionSelector";
import LogoScroller from "../../components/LogoScroller";
import Header from "../../components/LPHeader";
import HeroSection from "../../components/LPHeroSection";
import ContentSection from "../../components/LPContentSection";
import Footer from "../../components/LPFooter";
import SubmissionReceived from "./SubmissionReceived";
import Image1 from "../../assets/images/group_city_no_bg.png";
import Image2 from "../../assets/images/celebrating_group_no_bg.png";
import Image3 from "../../assets/images/marketing_laptop_no_bg.png";
import { supabase } from "../../lib/supabase";
import BaruchLogo from "../../assets/images/BaruchCollegeLogo.png";
import BronxCCLogo from "../../assets/images/BronxCommunityCollegeLogo.png";
import BrooklynLogo from "../../assets/images/BrooklynCollegeLogo.png";
import CCNYLogo from "../../assets/images/CityCollegeofNewYorkLogo.png";
import CSILogo from "../../assets/images/CSILogo.png";
import HunterLogo from "../../assets/images/HunterLogo.png";
import JohnJayLogo from "../../assets/images/JohnJayLogo.png";
import LehmanLogo from "../../assets/images/LehmanCollegeLogo.png";
import "./LandingPage.css";

// Add these validation functions at the top of both files
const validateName = (name) => {
  return /^[a-zA-Z\s-']+$/.test(name); // Only letters, spaces, hyphens, and apostrophes
};

const validateEmail = (email) => {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(edu|org|com)$/.test(email);
};

const SubTypeButton = ({ title, onClick, isSelected }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      border rounded-md py-2 px-3 xs:p-2 sm:p-4 w-full text-center transition-all duration-150 ease-in-out text-lg xs:text-sm sm:text-base md:text-lg
      ${
        isSelected
          ? "border-[#5379F6] bg-[#5379F6]/10 shadow-lg"
          : "border-gray-300 bg-white hover:border-[#5379F6] hover:shadow-md"
      }
      focus:outline-none focus:ring-1 focus:ring-indigo-500
    `}
  >
    <h4 className="font-medium text-black">{title}</h4>
  </button>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [schoolEmail, setEmail] = useState("");
  const [schoolStatus, setStatus] = useState("");
  const [institution, setInstitution] = useState("");
  const [position, setPosition] = useState("");
  const [receiveUpdates, setReceiveUpdates] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false); // Add this new state

  // Add refs for the images
  const image2Ref = useRef(null);
  const image3Ref = useRef(null);

  // Add useEffect for animation
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2,
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate");
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    if (image2Ref.current) observer.observe(image2Ref.current);
    if (image3Ref.current) observer.observe(image3Ref.current);

    return () => {
      if (image2Ref.current) observer.unobserve(image2Ref.current);
      if (image3Ref.current) observer.unobserve(image3Ref.current);
    };
  }, []);

  // HndleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (
      !fullName ||
      !schoolEmail ||
      !schoolStatus ||
      !institution ||
      !position
    ) {
      setError("Please fill in all required fields.");
      setLoading(false);
      return;
    }

    // Client-side validation
    if (!validateName(fullName)) {
      setError("Invalid name format.");
      setLoading(false);
      return;
    }

    if (!validateEmail(schoolEmail)) {
      setError(
        "Please use a valid email address. For now, a personal email (Gmail) is recommended."
      );
      setLoading(false);
      return;
    }

    try {
      const submissionData = {
        name: fullName,
        school_email: schoolEmail,
        school_type: schoolStatus,
        institution,
        position,
        receive_updates: receiveUpdates,
      };

      const submissionType = "single";

      const { error: functionError } = await supabase.functions.invoke(
        "send-waitlist-verification",
        {
          body: { submissionData, submissionType },
        }
      );

      if (functionError) {
        if (functionError.message.includes("already been submitted")) {
          throw new Error("This email address is already on the waitlist.");
        }
        throw functionError;
      }

      // Set the submitted state to true to show the success component
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error:", error);
      setError(
        error.message || "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Update navigation handlers to use query parameters
  const handleNavigation = (formType) => {
    switch (formType) {
      case "ambassador":
        navigate("/join-us?form=ambassador");
        break;
      case "admin":
        navigate("/join-us?form=admin");
        break;
      default:
        navigate("/join-us");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 mx-auto ">
      <Header />

      <main className="flex-grow ">
        <HeroSection />

        <ContentSection
          title="Scholarty is on the way"
          bgColor="bg-white"
          textColor="text-black"
        >
          <p className="xl:text-xl 2xl:text-2xl text-center">
            We're hard at work building the platform that will make student life
            more{" "}
            <span className="hidden sm:inline">
              <br></br>
            </span>
            exciting, connected, and opportunity-rich than ever.
          </p>
        </ContentSection>

        <section className="bg-white xs:pb-8 sm:pb-10 md:pb-16">
          <div className="max-w-9xl mx-auto px-6 sm:px-6 lg:px-8">
            <h2 className="xl:text-lg 2xl:text-xl font-inter text-center mb-4 sm:mb-6 md:mb-8 text-[#5c5c5c]">
              Tailoring for leading institutions
            </h2>

            <LogoScroller
              logos={[
                {
                  src: BaruchLogo,
                  alt: "Baruch College",
                },
                {
                  src: BronxCCLogo,
                  alt: "Bronx Community College",
                },
                {
                  src: BrooklynLogo,
                  alt: "Brooklyn College",
                },
                {
                  src: CCNYLogo,
                  alt: "City College of New York",
                },
                {
                  src: CSILogo,
                  alt: "College of Staten Island",
                },
                {
                  src: HunterLogo,
                  alt: "Hunter College",
                },
                {
                  src: JohnJayLogo,
                  alt: "John Jay College",
                },
                {
                  src: LehmanLogo,
                  alt: "Lehman College",
                },
              ]}
            />
          </div>
        </section>

        <ContentSection
          title="Who Are We Looking For?"
          bgColor="bg-white"
          textColor="text-black"
        >
          {/* Container for the three-column layout */}
          {/* items-center will vertically align the center of the 3 columns if their heights differ */}
          {/* flex flex-col items-center: stacks children vertically and centers them horizontally within this column */}
          {/* text-center: centers the text within the children blocks */}
          <div className="flex flex-col md:flex-row items-center gap-6 lg:gap-8">
            {/* --- Left Column --- */}
            <div className="w-full md:w-1/3 flex flex-col items-center space-y-4 md:space-y-8 text-center">
              {/* Top Text Block in Left Column */}
              <div className="p-6 w-full ">
                {/* max-w-sm for consistent block width */}
                <h4 className="text-2xl xl:text-2xl 2xl:text-3xl font-semibold mb-2 ">
                  Club{" "}
                  <span className="hidden sm:inline">
                    <br />
                  </span>
                  Leader
                </h4>
                <p className="text-base xl:text-xl 2xl:text-2xl">
                  Be the first to test and <br></br>shape the future of campus
                  leadership tech.
                </p>
              </div>
              {/* Bottom Text Block in Left Column */}

              <div className="p-6 w-full">
                <h4 className="text-2xl xl:text-2xl 2xl:text-3xl font-semibold mb-2 ">
                  Student Ambassadors
                </h4>

                <p className="text-base xl:text-xl 2xl:text-2xl">
                  Champion Scholarty on campus, earn leadership cred, expand
                  your network.
                </p>
              </div>
            </div>

            {/* --- Middle Column (Image) --- */}
            <div className="w-full md:w-2/3  my-6 md:my-0 flex justify-center items-center order-first md:order-none">
              {/* order-first md:order-none: makes image appear first on mobile stack if desired */}
              <img
                src={Image1}
                alt="Central theme visual"
                className="w-4/5 md:w-full h-auto mx-auto max-w-3xl"
              />
            </div>

            {/* --- Right Column --- */}
            {/* Mirrors the structure of the Left Column */}
            <div className="w-full md:w-1/3 flex flex-col items-center space-y-4 md:space-y-8 text-center">
              {/* Top Text Block in Right Column */}
              <div className="p-6  w-full ">
                <h4 className="text-2xl xl:text-2xl 2xl:text-3xl font-semibold mb-2 ">
                  Student{" "}
                  <span className="hidden sm:inline">
                    <br />
                  </span>
                  Governments
                </h4>
                <p className="text-base xl:text-xl 2xl:text-2xl">
                  Roll Scholarty out campus-wide, boost engagement, retention &
                  student life metrics.
                </p>
              </div>
              {/* Bottom Text Block in Right Column */}
              <div className="p-6  w-full ">
                <h4 className="text-2xl xl:text-2xl 2xl:text-3xl font-semibold mb-2">
                  Students
                </h4>
                <p className="text-base xl:text-xl 2xl:text-2xl">
                  Test new features, share feedback, shape what we build.
                </p>
              </div>
            </div>
          </div>
        </ContentSection>

        <ContentSection title="" bgColor="bg-white" textColor="text-black">
          {/* Container for the two-column grid layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-2 md:pb-10">
            {/* --- Left Grid Column --- */}
            <div className="flex">
              <div className="bg-white p-6 rounded-3xl shadow-xl flex flex-col w-full border-2 border-[#5379F6]">
                {/* Text Content Area - flex-grow pushes button to bottom */}
                <div className="flex-grow text-center">
                  <h3 className="text-2xl xl:text-4xl 2xl:text-5xl font-semibold mb-3">
                    Ambassador Program
                  </h3>
                  <p className="text-base xl:text-xl 2xl:text-2xl">
                    Lead the Scholarty movement on <br></br>campus. Grow your
                    network, build your <br></br>resume.
                  </p>
                </div>
                <button
                  onClick={() => handleNavigation("ambassador")}
                  className="mt-6 text-lg md:text-lg bg-[#5379F6] hover:bg-[#4A6ACB] text-white font-semibold py-3 px-6 md:px-8 lg:px-16 rounded-lg transition-colors duration-150 w-full sm:w-auto sm:self-center whitespace-nowrap"
                >
                  Become a Student Ambassador
                </button>
              </div>
            </div>

            {/* --- Right Grid Column --- */}
            <div className="flex">
              <div className="bg-white p-6 rounded-3xl shadow-xl flex flex-col w-full  border-2 border-[#5379F6]">
                {/* Text Content Area */}
                <div className="flex-grow text-center">
                  <h3 className="text-2xl xl:text-4xl 2xl:text-5xl font-semibold mb-3">
                    Call for Beta Testers
                  </h3>
                  <p className="text-base xl:text-xl 2xl:text-2xl">
                    We're looking for student clubs to help <br></br> shape the
                    future of campus life. <br></br> Join the many club leaders
                    that have <br></br> already signed up to test the beta.
                  </p>
                </div>
                {/* Button at the bottom of the box */}
                <button
                  onClick={() => handleNavigation("beta")}
                  className="mt-6 text-lg md:text-lg bg-[#5379F6] hover:bg-[#4A6ACB] text-white font-semibold py-3 px-6 md:px-8 lg:px-16 rounded-lg transition-colors duration-150 w-full sm:w-auto sm:self-center whitespace-nowrap"
                >
                  Join Club Beta Testing Waitlist
                </button>
              </div>
            </div>
          </div>
        </ContentSection>

        <ContentSection title="" bgColor="bg-white" textColor="text-black">
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6 justify-center items-start">
            {/* --- Left Column --- */}
            <div className="flex flex-col font-inter h-full">
              <div className="grid grid-cols-1 gap-4 items-center lg:block">
                <div className="pr-2 lg:pr-0">
                  <h3 className="font-semibold text-2xl xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-4xl 2xl:text-5xl mb-2 sm:mb-4 md:mb-6 lg:mb-8 px-4 md:px-0">
                    Are You a Student, Club Member or Club Leader?
                  </h3>
                  <p className="text-base xxs:text-sm xs:text-base sm:text-lg md:text-xl xl:text-xl 2xl:text-2xl mb-3 sm:mb-4 md:mb-6 lg:mb-8 px-4 md:px-0">
                    Sign up to be among the first to get updates, explore clubs
                    when we launch, and start connecting across campuses.
                  </p>
                  <p className="text-base xs:text-sm sm:text-base md:text-xl xl:text-xl 2xl:text-2xl px-4 md:px-0">
                    Help us shape the future of student innovation. Your journey
                    starts here!
                  </p>
                </div>

                {/* This image will be right side on iPhone, and all screens until lg breakpoint */}
                <div className="flex justify-center lg:justify-center">
                  <img
                    ref={image2Ref}
                    src={Image2}
                    alt="Celebrating group"
                    className="h-auto w-full w-[80%] md:w-[100%] lg:max-w-full object-cover slide-in-left"
                  />
                </div>
              </div>
            </div>

            {/* --- Right Column (Form) --- */}
            <div className="flex w-full justify-center lg:justify-end md:pl-0 lg:pl-6 xl:pl-12">
              {isSubmitted ? (
                <div className="bg-white rounded-lg shadow-[0_0_20px_rgba(0,0,0,0.1)] p-6 sm:p-8 md:p-10 lg:p-12 w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl border-2 border-gray-200 font-inter flex flex-col justify-center min-h-[1000px]">
                  <SubmissionReceived />
                </div>
              ) : (
                <form
                  className="bg-white rounded-lg shadow-[0_0_20px_rgba(0,0,0,0.1)] p-6 sm:p-8 md:p-10 lg:p-12 w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl space-y-4 sm:space-y-6 border-2 border-gray-200 font-inter"
                  onSubmit={handleSubmit}
                >
                  <h4 className="text-xl sm:text-2xl md:text-3xl xl:text-3xl 2xl:text-4xl font-semibold mb-4 sm:mb-6 md:mb-8 text-black">
                    Early-Access Waitlist Form
                  </h4>

                  {/* Form Fields */}
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label
                        htmlFor="fullName"
                        className="block xl:text-lg 2xl:text-xl font-medium text-[#4A4A4A]"
                      >
                        Full Name
                      </label>
                      <input
                        className="w-full border rounded-lg py-3 px-4 xl:text-lg 2xl:text-xl"
                        id="fullName"
                        name="fullName"
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-1">
                      <label
                        htmlFor="schoolEmail"
                        className="block xl:text-lg 2xl:text-xl font-medium text-[#4A4A4A]"
                      >
                        Email
                      </label>
                      <p className="text-sm text-[#4A4A4A] -mt-1 mb-2">
                        Note: Some .edu and .org emails aren’t working for now. Please use a personal email (like Gmail) to get verification link.
                      </p>
                      <input
                        className="w-full border rounded-lg py-3 px-4 xl:text-lg 2xl:text-xl"
                        id="schoolEmail"
                        name="schoolEmail"
                        type="text"
                        value={schoolEmail}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2 sm:space-y-4">
                      <label className="block xl:text-lg 2xl:text-xl font-medium text-[#4A4A4A]">
                        Highschool or College/University
                      </label>
                      <div className="flex flex-row space-x-2 sm:space-x-4">
                        <SubTypeButton
                          title="High School"
                          onClick={async () => {
                            const currentStatus = schoolStatus;
                            setStatus("highschool");

                            if (institution && currentStatus !== "highschool") {
                              const { data, error } = await supabase
                                .from("institutions")
                                .select("type")
                                .eq("name", institution)
                                .single();

                              if (
                                error ||
                                !data ||
                                data.type !== "highschool"
                              ) {
                                setInstitution(""); // Clear invalid institution
                                setError(
                                  "Please select a high school institution"
                                );
                              }
                            }
                          }}
                          isSelected={schoolStatus === "highschool"}
                        />
                        <SubTypeButton
                          title="College/University"
                          onClick={async () => {
                            const currentStatus = schoolStatus;
                            setStatus("college");

                            if (institution && currentStatus !== "college") {
                              const { data, error } = await supabase
                                .from("institutions")
                                .select("type")
                                .eq("name", institution)
                                .single();

                              if (error || !data || data.type !== "college") {
                                setInstitution(""); // Clear invalid institution
                                setError(
                                  "Please select a college/university institution"
                                );
                              }
                            }
                          }}
                          isSelected={schoolStatus === "college"}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label
                        htmlFor="institution"
                        className="block xl:text-lg 2xl:text-xl font-medium text-[#4A4A4A]"
                      >
                        Institution
                      </label>
                      <InstitutionSelector
                        schoolType={
                          schoolStatus === "highschool"
                            ? "highschool"
                            : "college"
                        }
                        required
                        onSelect={(institution) => {
                          setInstitution(institution.name);
                        }}
                        className="w-full border rounded-lg py-3 px-4 xl:text-lg 2xl:text-xl"
                      />
                    </div>

                    <div className="space-y-4">
                      <label className="block xl:text-lg 2xl:text-xl font-medium text-[#4A4A4A]">
                        Which one describes you best?
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={position === "student"}
                            onChange={() => setPosition("student")}
                            className="w-5 h-5 accent-[#5379F6]"
                          />
                          <label className="xl:text-lg 2xl:text-xl text-[#4A4A4A]">
                            Student
                          </label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={position === "club_leader"}
                            onChange={() => setPosition("club_leader")}
                            className="w-5 h-5 accent-[#5379F6]"
                          />
                          <label className="xl:text-lg 2xl:text-xl text-[#4A4A4A]">
                            Club Leader
                          </label>
                        </div>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={position === "student_government"}
                            onChange={() => setPosition("student_government")}
                            className="w-5 h-5 accent-[#5379F6]"
                          />
                          <label className="xl:text-lg 2xl:text-xl text-[#4A4A4A]">
                            Student Government
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="my-6" />

                  <div className="flex items-center space-x-3 mb-8">
                    <input
                      type="checkbox"
                      id="updates"
                      name="receiveUpdates"
                      className="w-5 h-5 accent-[#5379F6]"
                      checked={receiveUpdates}
                      onChange={(e) => setReceiveUpdates(e.target.checked)}
                    />
                    <label
                      htmlFor="updates"
                      className="xl:text-xl 2xl:text-xl text-[#4A4A4A]"
                    >
                      I'd like to receive updates about Scholarty
                    </label>
                  </div>

                  <div className="flex flex-col items-center space-y-4">
                    <button
                      type="submit"
                      className="bg-[#5379F6] hover:bg-[#4A6ACB] text-white font-semibold text-lg xs:text-base sm:text-lg xl:text-lg 2xl:text-xl py-3 px-4 xs:px-8 sm:px-16 md:px-24 rounded-lg transition-colors duration-150 w-full xs:w-[90%] sm:w-[80%]"
                    >
                      Join The Waitlist
                    </button>
                    {error && (
                      <div className="text-red-500 text-center">{error}</div>
                    )}
                    {success && (
                      <div className="text-green-500 text-center">
                        {success}
                      </div>
                    )}
                    {loading && (
                      <div className="text-blue-500 text-center">
                        Submitting...
                      </div>
                    )}
                  </div>
                </form>
              )}
            </div>
          </div>
        </ContentSection>

        <ContentSection title="" bgColor="bg-white" textColor="text-black">
          <div className="container flex flex-col lg:flex-row items-center gap-6 2xl:mt-16">
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              <h2 className="text-2xl xl:text-4xl 2xl:text-5xl font-semibold">
                Sneak Peak: Scholarty Score{" "}
              </h2>
              <h2 className="text-xl xl:text-3xl 2xl:text-4xl">
                Lead. Grow. Get Rewarded
              </h2>

              <p className="py-4 xl:text-xl 2xl:text-2xl">
                A dynamic scoring system that celebrates active clubs and
                unlocks extra funding, awards, and features.
              </p>
              <ul className="list-disc pl-8 marker:text-[#5379F6] text-[#5C5C5C] inline-block text-left">
                <li className="pb-2 xl:text-xl 2xl:text-2xl">
                  Automatic activity tracking
                </li>
                <li className="pb-2 xl:text-xl 2xl:text-2xl">
                  Semester based club score
                </li>
                <li className="pb-2 xl:text-xl 2xl:text-2xl">
                  Higher scores unlock funding, visibility & perks
                </li>
              </ul>
              <p className="py-4 xl:text-xl 2xl:text-2xl">
                The more your club does, the higher your score and the more
                support scholarty provides
              </p>
            </div>
            {/*--- Right Column (image)--- */}
            <div className="w-full lg:w-1/2 flex justify-center">
              <img
                ref={image3Ref}
                src={Image3}
                alt="Marketing laptop"
                className="w-full w-[80%] lg:max-w-full lg:w-11/12 slide-in-right"
              />
            </div>
          </div>
        </ContentSection>
        <div className="bg-white pt-6 pb-14 md:pd-20">
          <div className="container mx-auto flex flex-col ">
            <div className="pb-4 md:pd-8">
              {/* <h2 className="text-xl xl:text-4xl 2xl:text-5xl font-semibold px-4 md:px-0"> */}
              <h2 className="text-2xl xl:text-4xl 2xl:text-5xl font-semibold px-4 md:px-0">
                Are You a College or High School Admin?
              </h2>
              {/* <h2 className="text-lg xl:text-3xl 2xl:text-4xl pl-10 md:pl-0 px-6 md:px-0"> */}
              <h2 className="text-md xl:text-3xl 2xl:text-4xl px-4 md:px-0">
                Learn More About What We Are Building!
              </h2>
            </div>
            <p className="text-md xl:text-xl 2xl:text-2xl px-4 md:px-0 text-left">
              We're building better ways for students to connect, lead, and
              grow. If you're an administrator interested in{" "}
              <span className="hidden md:block"></span>
              how Scholarty can support your campus life, let's chat!
            </p>
            <p className="text-md xl:text-xl 2xl:text-2xl px-4 md:px-0 py-4 text-left">
              We'd love to share our vision!
            </p>
            <div className="px-4 md:px-0 md:text-left">
              <button
                onClick={() => handleNavigation("admin")}
                className="bg-[#5379F6] hover:bg-[#4A6ACB] text-lg md:text-lg text-white font-semibold rounded-lg px-20 py-2 md:px-16 md:py-4"
              >
                Reach Us Here
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
